import React from 'react';
import { Alert, Link } from '@amzn/awsui-components-console';
import { v4 as uuidv4 } from 'uuid';
import { useForm, FormProvider } from 'react-hook-form';
import { CreateOperationResponse } from '@amzn/snow-data-service-typescript-client';

import { SDS_APIGATEWAY_ENDPOINT } from 'src/config';
import { useAuthContext } from 'src/providers/AuthContext';
import { logger } from 'src/utils/logger';
import useFetchOnUserActionUsingSDS from '../../../../hooks/useFetchOnUserActionUsingSDS';
import { useNavigation } from '../../navigation/hooks/useNavigation';
import { ROUTES } from '../../navigation/types/navigationTypes';
import { useStore } from 'src/stores/store';

import AddOperationPageForm from './AddOperationPageForm';
import {
	convertToCreateOperationRequest,
	DEFAULT_OPERATION_DATA,
	OperationProfileFormMetadata,
} from './OperationFormGenerationUtils';

interface AddOperationPageProps {
	onCancel: () => void;
	applicationId: string | null;
	applicationName: string;
}

const AddOperationPage: React.FC<AddOperationPageProps> = ({
	onCancel,
	applicationId,
	applicationName,
}) => {
	const { result: createOperationResult, onClickCB: createOperationOnClickCB } =
		useFetchOnUserActionUsingSDS<CreateOperationResponse>();
	const { userName } = useAuthContext();
	const { navigate } = useNavigation();
	const { addNavigationError } = useStore();

	const methods = useForm<OperationProfileFormMetadata>({
		defaultValues: {
			...DEFAULT_OPERATION_DATA,
			createdBy: userName,
			lastUpdatedBy: userName,
		},
	});

	const { getValues } = methods;

	const onSubmit = async () => {
		if (!applicationId) {
			return;
		}

		const formData = getValues();
		const createOperationRequest = convertToCreateOperationRequest(
			formData,
			applicationId,
			uuidv4()
		);

		const response = await createOperationOnClickCB({
			path: '/operation',
			method: 'POST',
			body: createOperationRequest,
			url: SDS_APIGATEWAY_ENDPOINT,
		});

		if (response?.error) {
			logger.error(`Error creating operation:${response.error}`);
			// Error banner is already displayed by the hook's built-in error handling
		} else {
			addNavigationError({
				message: (
					<>
						Operation{' '}
						<Link
							variant="primary"
							onFollow={() =>
								navigate(
									ROUTES.APPLICATION_DETAILS,
									`/applicationManagement/${applicationId}`
								)
							}
						>
							{formData.operationName}
						</Link>{' '}
						created successfully!
					</>
				),
				type: 'success',
				timestamp: Date.now(),
			});

			navigate(
				ROUTES.APPLICATION_DETAILS,
				`/applicationManagement/${applicationId}`
			);
		}
	};

	return !applicationId ? (
		<Alert type="error">
			No application selected. Please select an application first.
		</Alert>
	) : (
		<>
			<FormProvider {...methods}>
				<AddOperationPageForm
					onCancel={onCancel}
					onSubmit={onSubmit}
					applicationName={applicationName}
					isLoading={createOperationResult.isLoading}
					errorMessage={createOperationResult.error}
				/>
			</FormProvider>
		</>
	);
};

export default AddOperationPage;
